import { Expense } from "@common/utils/graphql";
import {
  atom,
  selectorFamily,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";

export type OutOfPolicyState = {
  reportId: number | null;
  expenseIdToReasonMap: Record<number, string | null>;
};
const defaultState = {
  reportId: null,
  expenseIdToReasonMap: {},
};

export const outOfPolicyState = atom<OutOfPolicyState>({
  key: "outOfPolicyState",
  default: defaultState,
});

export const isReportOutOfPolicyState = selectorFamily({
  key: "isReportOutOfPolicyState",
  get:
    (reportId: number) =>
    ({ get }) => {
      const outOfPolicy = get(outOfPolicyState);
      return outOfPolicy.reportId === reportId;
    },
});

export const useOutOfPolicyExpense = () => {
  const outOfPolicy = useRecoilValue(outOfPolicyState);

  const getExpenseOutOfPolicyReason = (
    expenseId: number | Pick<Expense, "id">,
  ) => {
    if (typeof expenseId === "object") {
      expenseId = expenseId.id;
    }
    return outOfPolicy.expenseIdToReasonMap[expenseId];
  };

  const isExpenseOutOfPolicy = (expenseId: number | Pick<Expense, "id">) => {
    if (typeof expenseId === "object") {
      expenseId = expenseId.id;
    }
    return !!outOfPolicy.expenseIdToReasonMap[expenseId];
  };

  return {
    getExpenseOutOfPolicyReason,
    isExpenseOutOfPolicy,
  };
};

// When an expense is updated or deleted, we remove the out of policy reason
export const useUpdateExpenseOutOfPolicyState = () => {
  const setOutOfPolicyState = useSetRecoilState(outOfPolicyState);
  const { isExpenseOutOfPolicy } = useOutOfPolicyExpense();

  const updateExpenseOutOfPolicyState = (expenseId: number) => {
    if (isExpenseOutOfPolicy(expenseId)) {
      setOutOfPolicyState((prevState) => {
        // Mark the expense as resolved
        const expenseIdToReasonMap = {
          ...prevState.expenseIdToReasonMap,
          [expenseId]: null,
        };

        // Check if all expenses are resolved
        const allReportExpensesResolved = Object.values(
          expenseIdToReasonMap,
        ).every((reason) => !reason);
        return {
          // If all expenses are resolved, clear the reportId
          reportId: allReportExpensesResolved ? null : prevState.reportId,
          expenseIdToReasonMap,
        };
      });
    }
  };

  return updateExpenseOutOfPolicyState;
};
